import { styled } from 'baseui'
import type { Theme } from 'baseui'
import { pulseLighting } from '@/shared/theme/pulseLighting'
import { pulseOutline, pulseRadius } from '../styled-components'

interface ThemeProps {
  $theme: Theme
}

export type StatusDotToken = 'successPulse' | 'missPulseBorder' | 'dangerPulse' | 'borderPulseHover'

export const FiltersStack = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  marginTop: '14px',
})

export const FiltersDivider = styled('hr', ({ $theme }: ThemeProps) => ({
  width: '100%',
  height: '1px',
  marginTop: 0,
  marginBottom: 0,
  borderTopWidth: 0,
  borderBottomWidth: 0,
  borderLeftWidth: 0,
  borderRightWidth: 0,
  backgroundColor: $theme.colors.borderPulseDivider,
}))

// Same look as PulseFieldLabel, as a <label> tied to the date input.
export const DateFieldLabel = styled('label', ({ $theme }: ThemeProps) => ({
  fontSize: '13px',
  fontWeight: 500,
  lineHeight: '18px',
  color: $theme.colors.textPulseLabel,
}))

export const OptionList = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
})

export const StatusOptionButton = styled<'button', { $selected: boolean }>('button', ({ $theme, $selected }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  width: '100%',
  height: '34px',
  paddingTop: 0,
  paddingBottom: 0,
  paddingLeft: '10px',
  paddingRight: '10px',
  ...pulseOutline('transparent'),
  ...pulseRadius('8px'),
  backgroundColor: $selected ? $theme.colors.bgPulseSelected : 'transparent',
  color: $selected ? $theme.colors.primaryPulse : $theme.colors.textPulseStrong,
  fontFamily: 'inherit',
  fontSize: '14px',
  fontWeight: $selected ? 600 : 400,
  textAlign: 'left',
  cursor: 'pointer',
  ':hover': { backgroundColor: $selected ? $theme.colors.bgPulseSelected : $theme.colors.bgPulseCanvas },
  ':focus-visible': { boxShadow: pulseLighting($theme).shadowPulseFocus },
}))

export const RadioMark = styled<'span', { $selected: boolean }>('span', ({ $theme, $selected }) => ({
  flexShrink: 0,
  width: '16px',
  height: '16px',
  boxSizing: 'border-box',
  ...pulseRadius('9999px'),
  backgroundColor: $theme.colors.white,
  borderTopWidth: $selected ? '5px' : '1.5px',
  borderBottomWidth: $selected ? '5px' : '1.5px',
  borderLeftWidth: $selected ? '5px' : '1.5px',
  borderRightWidth: $selected ? '5px' : '1.5px',
  borderTopStyle: 'solid',
  borderBottomStyle: 'solid',
  borderLeftStyle: 'solid',
  borderRightStyle: 'solid',
  borderTopColor: $selected ? $theme.colors.primaryPulse : $theme.colors.borderPulseHover,
  borderBottomColor: $selected ? $theme.colors.primaryPulse : $theme.colors.borderPulseHover,
  borderLeftColor: $selected ? $theme.colors.primaryPulse : $theme.colors.borderPulseHover,
  borderRightColor: $selected ? $theme.colors.primaryPulse : $theme.colors.borderPulseHover,
}))

export const StatusDot = styled<'span', { $token: StatusDotToken }>('span', ({ $theme, $token }) => ({
  flexShrink: 0,
  width: '8px',
  height: '8px',
  marginLeft: 'auto',
  ...pulseRadius('9999px'),
  backgroundColor: $theme.colors[$token],
}))

export const ChoiceOptionButton = styled<'button', { $selected: boolean }>('button', ({ $theme, $selected }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  width: '100%',
  height: '30px',
  paddingTop: 0,
  paddingBottom: 0,
  paddingLeft: '10px',
  paddingRight: '10px',
  ...pulseOutline('transparent'),
  ...pulseRadius('8px'),
  backgroundColor: $selected ? $theme.colors.bgPulseSelected : 'transparent',
  color: $selected ? $theme.colors.primaryPulse : $theme.colors.textPulseStrong,
  fontFamily: 'inherit',
  fontSize: '13.5px',
  fontWeight: $selected ? 600 : 400,
  textAlign: 'left',
  cursor: 'pointer',
  ':hover': { backgroundColor: $selected ? $theme.colors.bgPulseSelected : $theme.colors.bgPulseCanvas },
  ':focus-visible': { boxShadow: pulseLighting($theme).shadowPulseFocus },
}))
